import { InlineNotification, Layer, PasswordInput, TextInput } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useRef, useState, type FormEvent } from 'react';
import { PosActionButton } from '../actions/PosActionButton';
import './StaffLogin.scss';

export interface StaffLoginProps {
  /** Filled in when the terminal locks (FR-AUTH-05); another staff code switches user. */
  initialStaffCode?: string;
  locked?: boolean;
  /** Resolves with a translated error message, or undefined on success. */
  onSubmit(staffCode: string, password: string): Promise<string | undefined>;
}

/** Staff code and password login (DESIGN_SYSTEM.md §8.2, FR-AUTH-01). */
export function StaffLogin({ initialStaffCode = '', locked = false, onSubmit }: StaffLoginProps) {
  const { t } = useTranslation();
  const [staffCode, setStaffCode] = useState(initialStaffCode);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!staffCode.trim() || !password || busy) return;
    setBusy(true);
    const message = await onSubmit(staffCode.trim(), password);
    setBusy(false);
    if (message) {
      setError(message);
      setPassword('');
      passwordRef.current?.focus();
    }
  };

  return (
    <form className="pos-staff-login" onSubmit={submit} noValidate>
      <h1 className="pos-staff-login__title">{locked ? t('login.lockedTitle') : t('login.title')}</h1>
      {locked && <p className="pos-staff-login__hint">{t('login.lockedHint')}</p>}
      {/* The form is a panel (layer-01); its fields take the next layer's colour (Carbon layering). */}
      <Layer className="pos-staff-login__fields">
        <TextInput
          id="pos-staff-code"
          size="lg"
          labelText={t('login.staffCode')}
          value={staffCode}
          autoComplete="username"
          autoCapitalize="characters"
          autoFocus={!initialStaffCode}
          onChange={(e) => setStaffCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              passwordRef.current?.focus();
            }
          }}
        />
        <PasswordInput
          id="pos-password"
          ref={passwordRef}
          size="lg"
          labelText={t('login.password')}
          showPasswordLabel={t('carbon.showPassword')}
          hidePasswordLabel={t('carbon.hidePassword')}
          value={password}
          autoComplete="current-password"
          autoFocus={Boolean(initialStaffCode)}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit(e);
          }}
        />
      </Layer>
      {error && <InlineNotification kind="error" lowContrast hideCloseButton title={error} />}
      <PosActionButton fill role="send" label={t('login.submit')} disabled={busy} onPress={() => void submit()} />
    </form>
  );
}
